import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, CheckCircle2, Circle, Trash2, Briefcase, ArrowRight } from "lucide-react";

const PHASES = [
  { id: "preboard", label: "Pre-boarding", color: "#00e5ff" },
  { id: "day1", label: "Day 1", color: "var(--accent)" },
  { id: "week1", label: "Week 1", color: "#ffc700" },
  { id: "day30", label: "30-Day Check", color: "#ff9f1c" },
  { id: "offboard", label: "Offboarding", color: "#ff3b5c" },
];

export default function Onboarding() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [positions, setPositions] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(user.id);
  const [tasks, setTasks] = useState([]);
  const [open, setOpen] = useState(false);
  const canManage = user.role === "admin" || user.role === "manager";

  useEffect(() => {
    if (canManage) api.get("/users").then(({ data }) => setUsers(data));
    api.get("/positions").then(({ data }) => setPositions(data)).catch(() => {});
  }, [canManage]);

  const load = () => api.get(`/onboarding/tasks/${selectedUserId}`).then(({ data }) => setTasks(data));
  useEffect(() => { if (selectedUserId) load(); }, [selectedUserId]);

  const toggle = async (t) => {
    if (t.virtual) return; // virtual tasks can only be completed on the profile page
    await api.patch(`/onboarding/tasks/${t.id}`, { completed: !t.completed });
    load();
  };
  const remove = async (id) => {
    await api.delete(`/onboarding/tasks/${id}`);
    load();
  };

  // Resolve selected user — self-serve vs. admin/manager viewing someone else
  const selectedUser = useMemo(() => {
    if (selectedUserId === user.id) return user;
    return users.find((u) => u.id === selectedUserId) || null;
  }, [selectedUserId, user, users]);

  const selectedPosition = useMemo(() => {
    if (!selectedUser?.position_id) return null;
    return positions.find((p) => p.id === selectedUser.position_id) || null;
  }, [selectedUser, positions]);

  // Synthetic "Sign staff agreement" task — automatic, non-editable, non-deletable
  const agreementTask = useMemo(() => {
    if (!selectedPosition) return null;
    if (!(selectedPosition.agreement_body || selectedPosition.agreement_url)) return null;
    return {
      id: "__agreement__",
      virtual: true,
      phase: "day1",
      title: `Sign staff agreement — ${selectedPosition.name}`,
      description: "Review the duties and responsibilities, then acknowledge to complete onboarding.",
      completed: !!selectedUser?.position_acknowledged_at,
      completed_at: selectedUser?.position_acknowledged_at,
      is_self: selectedUserId === user.id,
    };
  }, [selectedPosition, selectedUser, selectedUserId, user.id]);

  const grouped = useMemo(() => {
    const base = PHASES.reduce((acc, p) => {
      acc[p.id] = tasks.filter((t) => t.phase === p.id);
      return acc;
    }, {});
    if (agreementTask) {
      base[agreementTask.phase] = [agreementTask, ...(base[agreementTask.phase] || [])];
    }
    return base;
  }, [tasks, agreementTask]);

  const done = tasks.filter((t) => t.completed).length + (agreementTask?.completed ? 1 : 0);
  const total = tasks.length + (agreementTask ? 1 : 0);
  const pct = total ? Math.round((done / total) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="onboarding-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--accent)]">Journey</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Onboarding & Offboarding</h1>
        </div>
        <div className="flex items-center gap-3">
          {canManage && (
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              data-testid="user-picker"
              className="bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 text-sm outline-none focus:border-[color:var(--accent)]"
            >
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
          {canManage && (
            <button onClick={() => setOpen(true)} data-testid="new-task" className="px-4 py-2 bg-[color:var(--accent)] text-black rounded-lg font-bold flex items-center gap-2 hover:bg-[color:var(--accent-hover)]">
              <Plus size={16} /> Add Task
            </button>
          )}
        </div>
      </div>

      <div className="card-surface p-5">
        <div className="flex items-center justify-between mb-2 text-sm">
          <div className="font-bold">Progress</div>
          <div className="text-slate-400">{done} / {total} · <span className="text-[color:var(--accent)] font-bold">{pct}%</span></div>
        </div>
        <div className="h-2 bg-[#0b0d12] rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[color:var(--accent)] to-[#00e5ff] transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        {PHASES.map((p) => (
          <div key={p.id} className="card-surface p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
              <div className="text-xs uppercase tracking-widest text-slate-400">{p.label}</div>
              <span className="ml-auto text-xs text-slate-500">{grouped[p.id].length}</span>
            </div>
            <div className="space-y-2">
              {grouped[p.id].map((t) => {
                if (t.virtual) {
                  return (
                    <div
                      key={t.id}
                      className={`rounded-lg p-3 border ${t.completed ? "border-[#00e5ff]/40 bg-[#00e5ff]/8" : "border-[color:var(--accent)]/60 bg-[color:var(--accent)]/8"}`}
                      data-testid="task-agreement"
                    >
                      <div className="flex items-start gap-2">
                        <div className={t.completed ? "text-[#00e5ff]" : "text-[color:var(--accent)]"}>
                          {t.completed ? <CheckCircle2 size={18} /> : <Briefcase size={18} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: t.completed ? "#00e5ff" : "var(--accent)" }}>
                            {t.completed ? "Signed" : "Required"} · Staff agreement
                          </div>
                          <div className={`text-sm font-semibold ${t.completed ? "text-slate-300" : ""}`}>{t.title}</div>
                          {!t.completed && <div className="text-xs text-slate-400 mt-1">{t.description}</div>}
                          {t.completed && t.completed_at && (
                            <div className="text-[11px] text-slate-400 mt-1">
                              Acknowledged {new Date(t.completed_at).toLocaleDateString()} at {new Date(t.completed_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                            </div>
                          )}
                          {!t.completed && (
                            t.is_self ? (
                              <Link
                                to="/profile"
                                data-testid="task-agreement-cta"
                                className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[color:var(--accent)] hover:underline"
                              >
                                Review &amp; sign <ArrowRight size={12} />
                              </Link>
                            ) : (
                              <div className="mt-2 text-[11px] text-slate-500 italic">Awaiting {selectedUser?.name || "this teammate"}'s acknowledgement</div>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={t.id} className="bg-[#0b0d12] border border-[#2a3447] rounded-lg p-3" data-testid={`task-${t.id}`}>
                    <div className="flex items-start gap-2">
                      <button onClick={() => toggle(t)} data-testid={`toggle-${t.id}`} className={t.completed ? "text-[color:var(--accent)]" : "text-slate-500 hover:text-slate-300"}>
                        {t.completed ? <CheckCircle2 size={18} /> : <Circle size={18} />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-semibold ${t.completed ? "line-through text-slate-500" : ""}`}>{t.title}</div>
                        {t.description && <div className="text-xs text-slate-400 mt-1">{t.description}</div>}
                      </div>
                      {canManage && (
                        <button onClick={() => remove(t.id)} data-testid={`del-task-${t.id}`} className="text-slate-500 hover:text-[#ff3b5c]"><Trash2 size={14} /></button>
                      )}
                    </div>
                  </div>
                );
              })}
              {grouped[p.id].length === 0 && <div className="text-xs text-slate-600 text-center py-4">No tasks</div>}
            </div>
          </div>
        ))}
      </div>

      {open && <NewTaskModal userId={selectedUserId} onClose={() => setOpen(false)} onCreated={() => { load(); setOpen(false); }} />}
    </div>
  );
}

function NewTaskModal({ userId, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [phase, setPhase] = useState("day1");
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/onboarding/tasks", { user_id: userId, title, description, phase });
    onCreated();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <form onSubmit={submit} className="card-surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="task-modal">
        <h2 className="text-xl font-bold mb-4">New Task</h2>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" required data-testid="task-title" className="w-full mb-3 bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)]" />
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optional)" rows={3} data-testid="task-desc" className="w-full mb-3 bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)]" />
        <select value={phase} onChange={(e) => setPhase(e.target.value)} data-testid="task-phase" className="w-full bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)]">
          {PHASES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200">Cancel</button>
          <button type="submit" data-testid="task-submit" className="px-4 py-2 rounded-lg bg-[color:var(--accent)] text-black font-bold text-sm">Create</button>
        </div>
      </form>
    </div>
  );
}
